import { useState, useContext, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  CircularProgress,
  Select,
  MenuItem,
  FormHelperText,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import request from "../../utilities/requests";
import { toast } from "../../utilities/SnackBarUtils";
import { EventContext } from "../../contexts/EventContext";

const StaffingDialog = ({ open, close, eventId }) => {
  const { userData } = useContext(EventContext);
  const [staff, setStaff] = useState([]);
  const [assignedStaff, setAssignedStaff] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState("");

  useEffect(() => {
    if (open) {
      getAssigned();
      getStaff();
    }
  }, [open]);

  const getStaff = async () => {
    let url = "staff/org";
    if (userData.role === "superadmin") {
      url += `/?eventId=${eventId}`;
    }
    const result = await request(url);
    if (result.success) {
      setStaff(result.data);
    }
  };

  const getAssigned = async () => {
    const result = await request(`staff/?eventId=${eventId}`);
    if (result.success) {
      setAssignedStaff(result.data);
    }
  };

  const assignStaff = async (userId) => {
    const body = { eventId, userId };
    const result = await request(`staff/assign`, "post", body);
    if (result.success) {
      toast.success("Staff assigned successfully");
      getAssigned();
      setSelectedStaff("");
    } else {
      toast.error("Failed to assign staff");
    }
  };

  const removeStaff = async (userId) => {
    const body = { eventId, userId };
    const result = await request(`staff/remove`, "post", body);
    if (result.success) {
      toast.success("Staff removed successfully");
      getAssigned();
    } else {
      toast.error("Failed to remove staff");
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>Manage Event Staff</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Currently Assigned:
        </Typography>
        {assignedStaff.length === 0 ? (
          <Typography variant="body2" sx={{ mb: 2 }}>
            No staff currently assigned.
          </Typography>
        ) : (
          <List dense>
            {assignedStaff.map((staff) => (
              <ListItem key={staff.userId}>
                <ListItemText
                  primary={`${staff.firstName} ${staff.lastName}`}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => removeStaff(staff.userId)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
        <Typography variant="subtitle1" sx={{ mt: 3 }}>
          Assign New Staff:
        </Typography>
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Select Staff</InputLabel>
          <Select
            value={selectedStaff}
            label="Select Staff"
            onChange={(e) => setSelectedStaff(e.target.value)}
          >
            {staff.map((user) => (
              <MenuItem key={user._id} value={user._id}>
                {user.firstName} {user.lastName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          sx={{ mt: 2 }}
          variant="contained"
          disabled={!selectedStaff}
          onClick={() => assignStaff(selectedStaff)}
        >
          Assign
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default StaffingDialog;
